import React, { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  TextField,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Button,
  Paper,
} from '@mui/material';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Creators as ProductCreators } from 'store/products/products-duck.js';
import { Creators as CategoryCreators } from 'store/categories/categories-duck.js';
import PropTypes from 'prop-types';

const statusOptions = [
  { label: 'Todos', value: null },
  { label: 'Ativos', value: true },
  { label: 'Inativos', value: false },
];

const highlightedOptions = [
  { label: 'Todos', value: null },
  { label: 'Destacados', value: true },
  { label: 'Não destacados', value: false },
];

const ProductFilters = ({
  filters,
  productsSetFilters,
  categories,
  categoriesFetchAsync,
  productsFetchAsync,
}) => {
  const { control, handleSubmit, setValue, watch, reset } = useForm({
    defaultValues: filters,
  });

  useEffect(() => {
    Object.entries(filters).forEach(([key, value]) => {
      setValue(key, value ?? '');
    });
  }, [filters, setValue]);

  const selectedCategoryId = watch('categoryId');

  const onSubmit = (data) => {
    const newFilters = Object.fromEntries(
      Object.entries(data).filter(([, v]) => v !== '' && v !== undefined),
    );
    newFilters.page = 1;
    productsSetFilters(newFilters);
    productsFetchAsync(newFilters);
  };

  const handleClear = () => {
    const cleared = {
      name: '',
      categoryId: null,
      subCategoryId: null,
      active: null,
      highlighted: null,
      page: 1,
    };
    reset(cleared);
    productsSetFilters(cleared);
    productsFetchAsync(cleared);
  };

  useEffect(() => {
    categoriesFetchAsync();
  }, [categoriesFetchAsync]);

  const subCategories = useMemo(() => {
    const category = categories.find((c) => c.id === selectedCategoryId);
    return category?.subCategories || [];
  }, [categories, selectedCategoryId]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2} mb={2} component={Paper} p={2}>
        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField {...field} fullWidth label="Nome do produto" variant="outlined" />
            )}
          />
        </Grid>

        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
          <Controller
            name="categoryId"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Categoria</InputLabel>
                <Select {...field} label="Categoria">
                  <MenuItem value="">Todas</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>

        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
          <Controller
            name="subCategoryId"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth disabled={!selectedCategoryId}>
                <InputLabel>Subcategoria</InputLabel>
                <Select {...field} label="Subcategoria">
                  <MenuItem value="">Todas</MenuItem>
                  {subCategories.map((sub) => (
                    <MenuItem key={sub.id} value={sub.id}>
                      {sub.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>

        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
          <Controller
            name="active"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select {...field} label="Status">
                  {statusOptions.map((opt) => (
                    <MenuItem key={crypto.randomUUID()} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>

        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
          <Controller
            name="highlighted"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Destaque</InputLabel>
                <Select {...field} label="Destaque">
                  {highlightedOptions.map((opt) => (
                    <MenuItem key={crypto.randomUUID()} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>

        <Grid item size={{ xs: 12, sm: 6, md: 3 }} alignSelf="center">
          <Button type="submit" variant="contained" color="primary" fullWidth>
            Filtrar
          </Button>
        </Grid>

        <Grid item size={{ xs: 12, sm: 6, md: 3 }} alignSelf="center">
          <Button variant="outlined" fullWidth onClick={() => handleClear()}>
            Limpar
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

const mapStateToProps = (state) => ({
  filters: state.Product.filters,
  categories: state.Category.categories,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...ProductCreators, ...CategoryCreators }, dispatch);
ProductFilters.propTypes = {
  filters: PropTypes.object,
  productsSetFilters: PropTypes.func.isRequired,
  categories: PropTypes.array.isRequired,
  categoriesFetchAsync: PropTypes.func.isRequired,
  productsFetchAsync: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductFilters);
